<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappManagementAPI from 'dashboard/api/whatsappManagement';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ inbox: { type: Object, required: true } });
const { t } = useI18n();
const loading = ref(false);
const isBusiness = ref(false);
const profilePicture = ref('');
const profile = ref({
  name: '',
  status: '',
  address: '',
  email: '',
  description: '',
  websites: '',
  timezone: 'America/Sao_Paulo',
});
const privacy = ref({
  readreceipts: 'all',
  profile: 'all',
  status: 'all',
  online: 'all',
  last: 'all',
  groupadd: 'all',
});
const days = ref(
  ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => ({
    day,
    mode: 'specific_hours',
    open: '09:00',
    close: '18:00',
  }))
);
const errorMessage = error =>
  error.response?.data?.error || error.response?.data?.message || error.message;
const toMinutes = value =>
  String(
    value
      .split(':')
      .reduce(
        (total, part, index) =>
          index ? total + Number(part) : Number(part) * 60,
        0
      )
  );
const toTime = value =>
  `${String(Math.floor(Number(value || 0) / 60)).padStart(2, '0')}:${String(Number(value || 0) % 60).padStart(2, '0')}`;

const load = async () => {
  loading.value = true;
  try {
    const { data } = await WhatsappManagementAPI.account(props.inbox.id);
    const instance = data.instance?.instance || data.instance || {};
    const business = data.business || {};
    const businessHours =
      business.business_hours || business.businessHours || {};
    profile.value = {
      name: instance.profileName || instance.name || '',
      status: business.about || instance.statusMessage || '',
      address: business.address || '',
      email: business.email || '',
      description: business.description || '',
      websites: (business.website || business.websites || []).join('\n'),
      timezone: businessHours.timezone || 'America/Sao_Paulo',
    };
    profilePicture.value =
      instance.profilePicUrl || instance.profilePictureUrl || '';
    privacy.value = { ...privacy.value, ...(data.privacy || {}) };
    isBusiness.value = Boolean(business.isBusiness);
    const configs =
      businessHours.business_config ||
      businessHours.config ||
      businessHours.days ||
      [];
    if (configs.length) {
      days.value = days.value.map(item => {
        const saved = configs.find(
          config => (config.day_of_week || config.day) === item.day
        );
        return saved
          ? {
              day: item.day,
              mode: saved.mode,
              open: toTime(saved.open_time || saved.openTimeInMinutes),
              close: toTime(saved.close_time || saved.closeTimeInMinutes),
            }
          : item;
      });
    }
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const onPicture = event => {
  const [file] = event.target.files;
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    profilePicture.value = String(reader.result);
  };
  reader.readAsDataURL(file);
};

const save = async () => {
  loading.value = true;
  try {
    const picture = profilePicture.value.startsWith('data:')
      ? profilePicture.value.split(',').pop()
      : undefined;
    await WhatsappManagementAPI.updateAccount(props.inbox.id, {
      name: profile.value.name,
      status: profile.value.status,
      picture,
      business: isBusiness.value
        ? {
            address: profile.value.address,
            email: profile.value.email,
            description: profile.value.description,
            websites: profile.value.websites
              .split('\n')
              .map(item => item.trim())
              .filter(Boolean),
            timezone: profile.value.timezone,
            hours: days.value.map(item => ({
              day: item.day,
              mode: item.mode,
              ...(item.mode === 'specific_hours'
                ? {
                    openTimeInMinutes: toMinutes(item.open),
                    closeTimeInMinutes: toMinutes(item.close),
                  }
                : {}),
            })),
          }
        : undefined,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
    await load();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const removePicture = async () => {
  try {
    await WhatsappManagementAPI.removePicture(props.inbox.id);
    profilePicture.value = '';
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const savePrivacy = async () => {
  try {
    await WhatsappManagementAPI.updatePrivacy(props.inbox.id, privacy.value);
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

onMounted(load);
</script>

<template>
  <div class="grid max-w-6xl gap-6 xl:grid-cols-2">
    <section class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
      <h3 class="mb-5 text-base font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PROFILE') }}
      </h3>
      <div class="mb-5 flex items-center gap-4">
        <img
          v-if="profilePicture"
          :src="profilePicture"
          class="size-20 rounded-full object-cover"
        />
        <div
          v-else
          class="flex size-20 items-center justify-center rounded-full bg-n-alpha-2 text-2xl text-n-slate-10"
        >
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INITIALS') }}
        </div>
        <div>
          <input
            type="file"
            accept="image/*"
            class="block text-sm"
            @change="onPicture"
          /><button class="mt-2 text-sm text-n-ruby-9" @click="removePicture">
            {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.REMOVE_PICTURE') }}
          </button>
        </div>
      </div>
      <div class="space-y-4">
        <label class="block text-sm text-n-slate-11">
          <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NAME') }}</span>
          <input
            v-model="profile.name"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
        <label class="block text-sm text-n-slate-11">
          <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.STATUS_BIO') }}</span>
          <textarea
            v-model="profile.status"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
      </div>
    </section>

    <section class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
      <h3 class="mb-2 text-base font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.BUSINESS') }}
      </h3>
      <p
        v-if="!isBusiness"
        class="mb-4 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
      >
        {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NOT_BUSINESS') }}
      </p>
      <div v-else class="space-y-4">
        <label class="block text-sm text-n-slate-11">
          <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.ADDRESS') }}</span>
          <input
            v-model="profile.address"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
        <label class="block text-sm text-n-slate-11">
          <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.EMAIL') }}</span>
          <input
            v-model="profile.email"
            type="email"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
        <label class="block text-sm text-n-slate-11">
          <span>{{
            t('INBOX_MGMT.WHATSAPP_MANAGEMENT.BUSINESS_DESCRIPTION')
          }}</span>
          <textarea
            v-model="profile.description"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
        <label class="block text-sm text-n-slate-11">
          <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.WEBSITES') }}</span>
          <textarea
            v-model="profile.websites"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </label>
      </div>
    </section>

    <section
      v-if="isBusiness"
      class="rounded-xl border border-n-weak bg-n-surface-1 p-5 xl:col-span-2"
    >
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.BUSINESS_HOURS') }}
      </h3>
      <label class="mb-4 block max-w-md text-sm text-n-slate-11">
        <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.TIMEZONE') }}</span>
        <input
          v-model="profile.timezone"
          class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
        />
      </label>
      <div class="space-y-2">
        <div
          v-for="day in days"
          :key="day.day"
          class="grid items-center gap-2 md:grid-cols-[8rem_12rem_1fr_1fr]"
        >
          <span class="font-medium text-n-slate-11">{{
            t(`INBOX_MGMT.WHATSAPP_MANAGEMENT.DAYS.${day.day.toUpperCase()}`)
          }}</span>
          <select
            v-model="day.mode"
            class="rounded-lg border border-n-weak bg-n-background px-3 py-2"
          >
            <option value="specific_hours">
              {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SPECIFIC_HOURS') }}
            </option>
            <option value="open_24h">
              {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.OPEN_24H') }}
            </option>
            <option value="appointment_only">
              {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.APPOINTMENT_ONLY') }}
            </option>
          </select>
          <input
            v-if="day.mode === 'specific_hours'"
            v-model="day.open"
            type="time"
            class="rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
          <input
            v-if="day.mode === 'specific_hours'"
            v-model="day.close"
            type="time"
            class="rounded-lg border border-n-weak bg-n-background px-3 py-2"
          />
        </div>
      </div>
    </section>

    <section
      class="rounded-xl border border-n-weak bg-n-surface-1 p-5 xl:col-span-2"
    >
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PRIVACY') }}
      </h3>
      <div class="grid gap-4 md:grid-cols-3">
        <label
          v-for="field in [
            'readreceipts',
            'profile',
            'status',
            'online',
            'last',
            'groupadd',
          ]"
          :key="field"
          class="text-sm text-n-slate-11"
        >
          <span>{{
            t(
              `INBOX_MGMT.WHATSAPP_MANAGEMENT.PRIVACY_FIELDS.${field.toUpperCase()}`
            )
          }}</span>
          <select
            v-model="privacy[field]"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          >
            <option
              v-for="option in field === 'readreceipts'
                ? ['all', 'none']
                : field === 'online'
                  ? ['all', 'match_last_seen']
                  : ['all', 'contacts', 'contact_blacklist', 'none']"
              :key="option"
              :value="option"
            >
              {{
                t(
                  `INBOX_MGMT.WHATSAPP_MANAGEMENT.PRIVACY_VALUES.${option.toUpperCase()}`
                )
              }}
            </option>
          </select>
        </label>
      </div>
      <NextButton
        class="mt-4"
        variant="outline"
        :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVE_PRIVACY')"
        @click="savePrivacy"
      />
    </section>

    <div class="xl:col-span-2 flex justify-end">
      <NextButton
        :is-loading="loading"
        :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVE_ACCOUNT')"
        @click="save"
      />
    </div>
  </div>
</template>
